import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { ProgressData } from 'src/app/modules/shared/dtos/progress-data';
import { ResponseModel } from 'src/app/modules/shared/models/reponse.model';
import { ProgressIndicatingService } from 'src/app/modules/shared/services/progress-indicating.service';
import { Statuses } from '../dto/response-dto/unit-status-response-dto';
import { UnitStatusService } from '../services/unit-status.service';

@Component({
    selector: 'app-unit-status',
    templateUrl: './unit-status.component.html',
    styleUrls: ['./unit-status.component.scss']
})
export class UnitStatusComponent implements OnInit, OnDestroy {
    public pacList: Array<string> = [];
    public unitStatusInfo: Statuses[];
    public isDataAvailable: boolean = false;
    public noRecordsFound = false;
    private ngUnsubscribe = new Subject();
    private progress$ = new Subject<ProgressData>();
    public countryList: { name: string, code: string }[] = [];
    public column = [
        { field: 'contractId', header: 'Contract ID' },
        { field: 'pac', header: 'PAC' },
        { field: 'productCode', header: 'Product Code' },
        { field: 'contractService', header: 'Contract Service' },
        { field: 'statusDescription', header: 'Status' },
        { field: 'serviceCoverage', header: 'Service Coverage' },
        { field: 'manufacturer', header: 'Manufacturer' },
        { field: 'activationStartDate', header: 'Start Date (UTC)' },
        { field: 'contractEndDate', header: 'End Date (UTC)' }
    ];

    public constructor(
        public fb: FormBuilder,
        private unitStatusService: UnitStatusService,
        private progressService: ProgressIndicatingService,
        private messageService: MessageService

    ) { }

    public unitStatusForm = this.fb.group({
        pac: new FormControl('', [
            Validators.required,
            Validators.pattern(/^[a-zA-Z0-9]+$/),
            Validators.maxLength(13),
            Validators.minLength(12)
        ]),
        unitStatus: new FormControl('', null)
    });

    public ngOnInit(): void {
        window.history.pushState({}, '', '/common/unit-status/');
        this.isDataAvailable = false;
        this.fillpacList();
        this.loadCustomers();
    }

    public getUnitStatusData() : void {
        this.progressService.show({
            progress$: this.progress$.asObservable(),
            initial: {
                header: 'Loading PAC details',
                details: 'Please wait...',
                done: false
            }
        });
        const pacId = this.unitStatusForm.value.pac;
        this.unitStatusService.getPacData(pacId).pipe(takeUntil(this.ngUnsubscribe)).subscribe(result => {
            this.unitStatusInfo = result.statuses!;
            this.unitStatusInfo.forEach(ele => {
                if (ele.contractType === 'T') {
                    ele.contractService = ele.accessCode;
                }
                ele.countryName = this.countryList.find(country => country.code === ele.countryCode)?.name!;
            });
            if (result.responseCode === 0) {
                this.isDataAvailable = true;
                this.sendProgressDoneSignal();
            } else {
                this.isDataAvailable = false;
                this.noRecordsFound = true;
                this.sendProgressDoneSignal();
            }
        }, (error) => {
            this.noRecordsFound = true;
            // this.unitStatusForm.reset();
            this.messageService.add({ severity: 'error', summary: 'Fail', detail: error.error.responseMessage });
            this.sendProgressDoneSignal();
        });
    }

    private loadCustomers() : void {
        this.progressService.show({
            progress$: this.progress$.asObservable(),
            initial: {
                header: 'Loading Metadata Details',
                details: 'Please wait...',
                done: false
            }
        });
        this.unitStatusService.getConfigurationSelectionContract().pipe(
            filter(response => new ResponseModel(response).successful),
            map(res => res.selections)
        ).subscribe(data => {
            data?.countries.forEach(country => {
                this.countryList.push({ name: country.name!, code: country.countryabbr! });
            });
            this.sendProgressDoneSignal();
        }, (error) => {
            this.messageService.add({ severity: 'error', summary: 'Fail', detail: error.error.responseMessage });
            this.sendProgressDoneSignal();
        });
    }

    public get formControls() : {[key: string]: AbstractControl} {
        return this.unitStatusForm.controls;
    }

    public fillpacList(): void {
        let temp = [];
        this.pacList = [];
        temp = JSON.parse(localStorage.getItem('pacId')!);
        if (temp !== null) {
            temp.forEach((x: string) => {
                if (x) {
                    this.pacList.push(x);
                }
            });
            this.pacList.reverse();
        }
    }

    public onSearchPac(): void {
        this.noRecordsFound = false;
        this.isDataAvailable = false;
        this.fillpacList();
        this.updateSearchLocalStorage();
        this.getUnitStatusData();
        window.history.pushState('', '', '/common/unit-status/' + this.unitStatusForm.value.pac);
    }

    private updateSearchLocalStorage(): void {
        const pacSearchHasValue = this.unitStatusForm.value.pac;
        const localStorageNotContainsSearchedpac = !this.pacList.includes(
            this.unitStatusForm.value.pac
        );

        if (localStorageNotContainsSearchedpac && pacSearchHasValue) {
            this.pacList.push(pacSearchHasValue);
            const numberToShift = Math.max(0, this.pacList.length - 10);
            for (let i = 0; i < numberToShift; i++) {
                this.pacList.shift();
            }
            localStorage.setItem('pacId', JSON.stringify(this.pacList));
        }
    }

    public moveSelectedToTop(): void {
        const pacSearchValue = this.unitStatusForm.value.pac;
        const index = this.pacList.indexOf(pacSearchValue);
        if (pacSearchValue && index > 0) {
            this.pacList.splice(index, 1);
            this.pacList.unshift(pacSearchValue);
        }
    }

    public filterList(event: {query: string}): void {
        const query = event.query ? event.query : '';
        this.pacList = this.pacList.filter((item: string) =>
            item.toLowerCase().includes(query.toLowerCase())
        );
    }

    public ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    private sendProgressDoneSignal(): void {
        this.progress$.next({
            header: '',
            done: true
        });
    }
}
